import { useMemo } from 'react';

import { useTheme } from '@mui/material';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Line,
  RatioProgressBar,
  TimeXAxis,
  YAxis,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';
import { dashesOrNum, formatPercentage } from '@cast/utils';

import { K8sProviderIcon } from '@cast/features-cluster-ui';

import {
  clustersTileLoading,
  nodesTileLoading,
  utilizationTileLoading,
} from './DashboardMetricLoading';
import { FleetMetricRow, FleetMetricTile } from './FleetMetricTile';
import { ResourceMetricTile } from './ResourceMetricTile';
import { DashboardSectionError } from './DashboardSectionError';
import { useFleetDashboardMetrics } from '../hooks/useFleetDashboardMetrics';
import { formatFleetMetricNumber } from '../utils/fleetMetricFormat';
import { formatMonthlyCost } from '../utils/formatMonthlyCost';

const getTopProvider = (providerCounts: Map<string, number>) => {
  let topProvider = 'unknown';
  let topCount = 0;

  providerCounts.forEach((count, provider) => {
    if (count > topCount) {
      topProvider = provider;
      topCount = count;
    }
  });

  return { provider: topProvider, count: topCount };
};

export const DashboardMetricGrid = () => {
  const theme = useTheme();
  const {
    stats,
    resources,
    nodeCosts,
    utilizationSparkData,
    utilizationSparkDomain,
    isSummaryMetricsLoading,
    isUtilizationCardLoading,
    error,
    refetch,
  } = useFleetDashboardMetrics();

  const topProvider = useMemo(
    () => getTopProvider(stats.providerCounts),
    [stats.providerCounts]
  );

  const topProviderMonthlyCost =
    stats.providerCostMap.get(topProvider.provider) ?? 0;

  return (
    <DashboardSectionError hasError={!!error} onRetry={refetch}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, minmax(0, 1fr))"
        gap="16px"
        data-testid="dashboard-metric-grid"
        sx={{ '& > *': { minWidth: 0 } }}
      >
        <FleetMetricTile
          title="Clusters"
          value={stats.total}
          isLoading={isSummaryMetricsLoading}
          loading={clustersTileLoading}
          footer={
            <FleetMetricRow
              label={
                <Stack direction="row" alignItems="center" gap={6}>
                  <K8sProviderIcon
                    provider={topProvider.provider as K8sProvider}
                    size={12}
                  />
                  <span>
                    {topProvider.provider.toUpperCase()} · {topProvider.count}
                  </span>
                </Stack>
              }
              value={formatMonthlyCost(topProviderMonthlyCost)}
              unit="/mo"
            />
          }
        />

        <FleetMetricTile
          title="Nodes"
          value={stats.totalNodes}
          isLoading={isSummaryMetricsLoading}
          loading={nodesTileLoading}
          middle={
            <RatioProgressBar
              hideBorders
              bars={[
                {
                  value: stats.spotNodes,
                  color: theme.palette.blue[300],
                },
                {
                  value: stats.onDemandNodes,
                  color: theme.palette.blue[900],
                },
              ]}
              size="lg"
            />
          }
          footer={
            <>
              <FleetMetricRow
                label={`${stats.spotNodes} Spot`}
                markerColor={theme.palette.blue[300]}
                value={formatMonthlyCost(nodeCosts.spotMonthlyCost)}
                unit="/mo"
              />
              <FleetMetricRow
                label={`${stats.onDemandNodes} On-demand`}
                markerColor={theme.palette.blue[900]}
                value={formatMonthlyCost(nodeCosts.onDemandMonthlyCost)}
                unit="/mo"
              />
            </>
          }
        />

        <FleetMetricTile
          title="Utilization"
          isLoading={isUtilizationCardLoading}
          loading={utilizationTileLoading}
          middle={
            isUtilizationCardLoading ? (
              utilizationTileLoading.middle
            ) : utilizationSparkData.length ? (
              <Box height={56} width="100%" sx={{ overflow: 'visible' }}>
                <ChartContainer
                  data={utilizationSparkData}
                  margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                  fallbackChartType="line"
                  sx={{ height: '100%', overflow: 'visible' }}
                  testId="utilization-sparkline"
                >
                  <Line
                    dataKey="cpuUtilization"
                    stroke={theme.palette.blue[500]}
                    strokeWidth={2}
                    dot={false}
                    tooltipConfig={{ displayName: 'CPU' }}
                  />
                  <Line
                    dataKey="memoryUtilization"
                    stroke={theme.palette.blue[300]}
                    strokeWidth={2}
                    dot={false}
                    tooltipConfig={{ displayName: 'Memory' }}
                  />
                  <YAxis hide domain={utilizationSparkDomain} />
                  <TimeXAxis
                    hide
                    unixDataKey="chartTimestampUnix"
                    timezone="UTC"
                    padding={{ left: 0, right: 0 }}
                  />
                  <ChartTooltip<{
                    timestamp: string;
                    chartTimestampUnix: number;
                    cpuUtilization: number;
                    memoryUtilization: number;
                  }>
                    allowEscapeViewBox={{ x: false, y: true }}
                    header={(props) => (
                      <ChartTooltipTimestampHeader
                        {...props}
                        timezone="UTC"
                        hideTime
                      />
                    )}
                    formatValue={(value) =>
                      typeof value === 'number'
                        ? formatPercentage(value, { roundBy: 1 })
                        : '—'
                    }
                  />
                </ChartContainer>
              </Box>
            ) : (
              <Stack
                height={56}
                alignItems="center"
                justifyContent="center"
                width="100%"
              >
                <Typography variant="P12R" color="text.secondary">
                  No utilization data
                </Typography>
              </Stack>
            )
          }
          footer={
            <>
              <FleetMetricRow
                label="CPU"
                markerColor={theme.palette.blue[500]}
                value={dashesOrNum({ value: stats.cpuUtilPct, roundBy: 1 })}
                unit="%"
              />
              <FleetMetricRow
                label="Memory"
                markerColor={theme.palette.blue[300]}
                value={dashesOrNum({ value: stats.memUtilPct, roundBy: 1 })}
                unit="%"
              />
            </>
          }
        />

        <ResourceMetricTile
          testId="total-cpu-tile"
          title="Total CPU"
          unit={resources.cpu.unit}
          connected={resources.cpu.connected}
          discovered={resources.cpu.discovered}
          used={resources.cpu.used}
          isLoading={isSummaryMetricsLoading}
          formatTotal={(value) => formatFleetMetricNumber(value)}
          formatDiscovered={(value) => formatFleetMetricNumber(value)}
        />

        <ResourceMetricTile
          testId="total-memory-tile"
          title="Total Memory"
          unit={resources.memory.unit}
          connected={resources.memory.connectedTiB}
          discovered={resources.memory.discoveredTiB}
          used={resources.memory.usedTiB}
          discoveredUnit={resources.memory.discoveredUnit}
          isLoading={isSummaryMetricsLoading}
          formatTotal={(value) => formatFleetMetricNumber(value, 1)}
          formatDiscovered={(value) => formatFleetMetricNumber(value, 1)}
        />

        <ResourceMetricTile
          testId="total-gpu-tile"
          title="Total GPU"
          unit={resources.gpu.unit}
          connected={resources.gpu.connected}
          discovered={resources.gpu.discovered}
          used={resources.gpu.used}
          isLoading={isSummaryMetricsLoading}
          formatTotal={(value) => formatFleetMetricNumber(value)}
          formatDiscovered={(value) => formatFleetMetricNumber(value)}
        />
      </Box>
    </DashboardSectionError>
  );
};
